import {
  EventSubscriptionCheckboxGrid,
  getAdapterMeta,
  listEventTypesForApp,
  useLanguageAwareRouting,
} from '@gofranz/common-components';
import type { Form, FormIntegrationDto, IntegrationDto } from '@gofranz/formshive-common';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Select,
  Stack,
  Switch,
  Text,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconHistory, IconLink, IconPlus, IconTrash } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRustyState } from '../../state';

export interface FormsIntegrationsPanelProps {
  form: Form;
  /** Reports the live count back to the parent's accordion summary. */
  onIntegrationsChange?: (count: number) => void;
}

/**
 * Panel rendered inside the form-edit page accordion. Lists integrations
 * linked to this form with their event-type subscription mask + enabled
 * toggle, and lets the user link / unlink / jump to delivery history.
 */
export function FormsIntegrationsPanel(props: FormsIntegrationsPanelProps) {
  const { t } = useTranslation();
  const { api } = useRustyState.getState();
  const nav = useNavigate();
  const { createLanguageURL } = useLanguageAwareRouting();

  const [links, setLinks] = useState<FormIntegrationDto[]>([]);
  const [allIntegrations, setAllIntegrations] = useState<IntegrationDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [linkModalOpen, setLinkModalOpen] = useState(false);

  const formshiveEvents = useMemo(() => listEventTypesForApp('formshive'), []);

  const refreshLinks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listFormIntegrations(props.form.id);
      setLinks(res.data);
      props.onIntegrationsChange?.(res.data.length);
    } catch (e) {
      console.error('Failed to load form integrations', e);
    } finally {
      setLoading(false);
    }
  }, [api, props.form.id, props.onIntegrationsChange]);

  const refreshAll = useCallback(async () => {
    try {
      const res = await api.getIntegrations({ limit: 100, offset: 0 });
      setAllIntegrations(res.data);
    } catch (e) {
      console.error('Failed to load integrations', e);
    }
  }, [api]);

  useEffect(() => {
    refreshLinks();
  }, [refreshLinks]);

  const linkedIds = useMemo(() => new Set(links.map((l) => l.integration_id)), [links]);
  const linkable = useMemo(
    () => allIntegrations.filter((i) => !linkedIds.has(i.id)),
    [allIntegrations, linkedIds]
  );

  const openLinkModal = async () => {
    await refreshAll();
    setLinkModalOpen(true);
  };

  // -------------------------------------------------------------------------
  // Mutation helpers
  // -------------------------------------------------------------------------

  const updateLink = async (
    integrationId: string,
    body: { event_types?: string[]; enabled?: boolean }
  ) => {
    setBusyId(integrationId);
    try {
      await api.updateFormIntegration(props.form.id, integrationId, body);
      await refreshLinks();
    } catch (e) {
      console.error('Failed to update link', e);
    } finally {
      setBusyId(null);
    }
  };

  const unlink = async (integrationId: string) => {
    setBusyId(integrationId);
    try {
      await api.unlinkFormIntegration(props.form.id, integrationId);
      notifications.show({
        color: 'green',
        title: t('formshive.formIntegrations.unlinkSuccess.title'),
        message: t('formshive.formIntegrations.unlinkSuccess.body'),
      });
      await refreshLinks();
    } catch (e) {
      console.error('Failed to unlink integration', e);
    } finally {
      setBusyId(null);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (links.length === 0 && !loading) {
    return (
      <Stack gap="md">
        <Alert icon={<IconAlertCircle size={20} />} color="blue">
          {t('formshive.formIntegrations.empty')}
        </Alert>
        <Group>
          <Button leftSection={<IconLink size={16} />} onClick={openLinkModal}>
            {t('formshive.formIntegrations.linkButton')}
          </Button>
        </Group>
        <LinkIntegrationModal
          opened={linkModalOpen}
          onClose={() => setLinkModalOpen(false)}
          linkable={linkable}
          formId={props.form.id}
          onLinked={refreshLinks}
        />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      {links.map((link) => {
        const meta = getAdapterMeta(link.kind);
        return (
          <Card key={link.integration_id} withBorder shadow="xs" radius="md">
            <Stack gap="md">
              <Group justify="space-between" wrap="nowrap" align="flex-start">
                <Stack gap={2}>
                  <Group gap="xs">
                    <Text fw={500}>{link.integration_title}</Text>
                    <Badge variant="light" size="sm">
                      {meta ? t(meta.label) : link.kind}
                    </Badge>
                    {!link.enabled && (
                      <Badge variant="light" color="gray" size="sm">
                        {t('formshive.formIntegrations.disabled')}
                      </Badge>
                    )}
                  </Group>
                </Stack>
                <Group gap={4}>
                  <Tooltip label={t('integrations.actions.viewDeliveries')}>
                    <ActionIcon
                      variant="subtle"
                      onClick={() =>
                        nav(
                          createLanguageURL(
                            `/account/integrations/${link.integration_id}/deliveries`
                          )
                        )
                      }
                    >
                      <IconHistory size={16} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label={t('formshive.formIntegrations.unlinkTooltip')}>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      loading={busyId === link.integration_id}
                      onClick={() => unlink(link.integration_id)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Group>

              <Switch
                label={t('formshive.formIntegrations.enabledLabel')}
                description={t('formshive.formIntegrations.enabledHelp')}
                checked={link.enabled}
                disabled={busyId === link.integration_id}
                onChange={(e) =>
                  updateLink(link.integration_id, { enabled: e.currentTarget.checked })
                }
              />

              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  {t('formshive.formIntegrations.eventsLabel')}
                </Text>
                <EventSubscriptionCheckboxGrid
                  availableEvents={formshiveEvents}
                  selectedEventTypes={link.event_types}
                  onChange={(types) => updateLink(link.integration_id, { event_types: types })}
                  disabled={busyId === link.integration_id}
                />
              </Stack>
            </Stack>
          </Card>
        );
      })}

      <Group>
        <Button variant="light" leftSection={<IconPlus size={16} />} onClick={openLinkModal}>
          {t('formshive.formIntegrations.linkButton')}
        </Button>
      </Group>

      <LinkIntegrationModal
        opened={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        linkable={linkable}
        formId={props.form.id}
        onLinked={refreshLinks}
      />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Modal: link an existing integration with an initial event-type mask.
// ---------------------------------------------------------------------------

interface LinkModalProps {
  opened: boolean;
  onClose: () => void;
  linkable: IntegrationDto[];
  formId: string;
  onLinked: () => void;
}

function LinkIntegrationModal(props: LinkModalProps) {
  const { t } = useTranslation();
  const { api } = useRustyState.getState();
  const formshiveEvents = useMemo(() => listEventTypesForApp('formshive'), []);

  const [selected, setSelected] = useState<string | null>(null);
  const [eventTypes, setEventTypes] = useState<string[]>(() => formshiveEvents.map((e) => e.type));
  const [busy, setBusy] = useState(false);

  // Reset state when reopening so a stale selection from a previous run
  // doesn't leak into the next link.
  useEffect(() => {
    if (props.opened) {
      setSelected(null);
      setEventTypes(formshiveEvents.map((e) => e.type));
    }
  }, [props.opened, formshiveEvents]);

  const dropdownItems = props.linkable.map((i) => ({ value: i.id, label: i.title }));

  const link = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.linkFormIntegration(props.formId, {
        integration_id: selected,
        event_types: eventTypes,
        enabled: true,
      });
      notifications.show({
        color: 'green',
        title: t('formshive.formIntegrations.linkSuccess.title'),
        message: t('formshive.formIntegrations.linkSuccess.body'),
      });
      props.onLinked();
      props.onClose();
    } catch (e) {
      console.error('Failed to link integration', e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      opened={props.opened}
      onClose={props.onClose}
      title={t('formshive.formIntegrations.linkModal.title')}
      size="lg"
    >
      <Stack gap="md">
        {dropdownItems.length === 0 ? (
          <Alert icon={<IconAlertCircle size={20} />} color="blue">
            {t('formshive.formIntegrations.linkModal.noneAvailable')}
          </Alert>
        ) : (
          <>
            <Select
              label={t('formshive.formIntegrations.linkModal.selectLabel')}
              placeholder={t('formshive.formIntegrations.linkModal.selectPlaceholder')}
              data={dropdownItems}
              value={selected}
              onChange={setSelected}
              searchable
            />
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                {t('formshive.formIntegrations.eventsLabel')}
              </Text>
              <EventSubscriptionCheckboxGrid
                availableEvents={formshiveEvents}
                selectedEventTypes={eventTypes}
                onChange={setEventTypes}
              />
            </Stack>
          </>
        )}

        <Group justify="flex-end">
          <Button variant="subtle" onClick={props.onClose}>
            {t('integrations.actions.cancel')}
          </Button>
          <Button disabled={!selected || dropdownItems.length === 0} loading={busy} onClick={link}>
            {t('formshive.formIntegrations.linkModal.confirm')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
